<template>
	<div class="test-item">
		<v-card flat>
			<v-card-text v-if="type != 'custom'" :key="render">
				<template v-if="test.module == 'ryodoraku'">
					<RyodorakuResults
						:data="test.result"
						:clinic="true"
						:showGCharts="true"
					/>
				</template>
				<template v-if="test.module == 'tcm'">
					<v-row v-if="test.kind == 'tongue'" no-gutters align="center">
						<v-col>
							<v-row no-gutters align="center">
								<v-col md="auto" class="mr-4">
									<TongueTemplate
										:test="test.data"
									/>
								</v-col>
								<v-col>
									<TongueDetails :tongue="test.data" />
								</v-col>
							</v-row>
						</v-col>
						<v-col md="auto" v-if="test.data.photos.length > 0">
							<v-carousel
								height="120"
								style="width: 180px"
								hide-delimiter-background
								show-arrows-on-hover
								:show-arrows="test.data.photos.length > 1"
								hide-delimiters
							>
								<v-carousel-item
									v-for="(image, i) in test.data.photos"
									:key="i"
								>
									<v-img
										v-if="image.blob"
										:src="image.url"
										height="100"
										contain
									/>
									<v-img
										v-else
										:src="image.image"
										height="100"
										contain
									/>
								</v-carousel-item>
							</v-carousel>
						</v-col>
					</v-row>
					<v-row v-else-if="test.kind == 'pulse'" no-gutters >
						<TCMPulseDetails :test="test" />
					</v-row>
					<p v-else>
						<span style="white-space: pre">{{test.data.note}}</span>
					</p>
				</template>
				<template v-if="test.module == 'general'">
					<VitalSignsForm
						:test="test.result"
						:results="true"
						:clinic="true"
					/>
				</template>
				<template v-if="test.module == 'osteo'">
					<OsteopathyDetails :test="test.data" />
				</template>
			</v-card-text>
			<v-divider v-if="type != 'custom'"></v-divider>
			<v-card-actions>
				<v-tooltip top>
					<template v-slot:activator="{ on }">
						<v-btn icon @click="getResults" v-on="on" :loading="loadingResults">
							<v-icon color="primary">mdi-eye-outline</v-icon>
						</v-btn>
					</template>
					<span v-if="type === 'custom'" v-translate>Show</span>
					<span v-else v-translate>Show Results</span>
				</v-tooltip>
				<template v-if="type == 'tcm'">
					<template v-if="showResults">
						<TCMTongueForm
							key="results-tongue"
							v-if="test.kind == 'tongue'"
							@hide="showResults = false"
							:test="test"
							:results="true"
							@open-edit="showResults = false; showEdit = true;"
						/>
						<TCMPulseForm
							v-else-if="test.kind == 'pulse'"
							@hide="showResults = false"
							:test="test"
							:results="true"
						/>
						<TCMAbdominalPalpationForm
							v-else-if="test.kind == 'abdominal-palpation'"
							@hide="showResults = false"
							:test="test"
							:results="true"
						/>
						<TCMBodyStructureForm
							v-else-if="test.kind == 'body-structure'"
							@hide="showResults = false"
							:test="test"
							:results="true"
						/>
					</template>
				</template>
				<v-dialog v-else v-model="showResults" :max-width="type == 'osteo' ? '95%' : '80%'" scrollable>
					<CustomTestForm
						v-if="type === 'custom'"
						@hide="showResults = false"
						:test="test"
						:results="true"
						@open-edit="showResults = false; showEdit = true;"
					/>
					<VitalSignsForm
						v-if="type === 'general'"
						@hide="showResults = false"
						:test="test.data"
						:results="true"
					/>
					<v-card v-if="type === 'ryodoraku'">
						<v-card-title class="title primary--text secondary" v-translate>Ryodoraku Results</v-card-title>
						<v-card-text class="px-8">
							<RyodorakuResults
								:data="test.result"
							/>
						</v-card-text>
						<v-divider></v-divider>
						<v-card-actions>
							<v-btn color="primary darken-1" text @click="showResults = false">
								<translate>Close</translate>
							</v-btn>
						</v-card-actions>
					</v-card>
					<OsteopathyForm
						v-if="type === 'osteo'"
						@hide="showResults = false"
						:test="test.data"
						:results="true"
						:gender="gender"
						ref="osteoFormResults"
					/>
				</v-dialog>
				<v-tooltip v-if="type === 'custom'" top>
					<template v-slot:activator="{ on }">
						<v-btn icon @click="getCustomResults" v-on="on" :loading="loadingCustomResults">
							<v-icon color="primary">mdi-clipboard-list-outline</v-icon>
						</v-btn>
					</template>
					<span v-translate>Show Results</span>
					<v-dialog v-model="showCustomResults" max-width="50%" scrollable>
						<v-card>
							<v-card-title class="title primary--text secondary">
								{{test.name}} <span class="mx-1">-</span> <translate>Results</translate>
							</v-card-title>
							<v-card-text class="pt-4">
								<ICDList context="test-results" :list="icdList" />
							</v-card-text>
							<v-divider></v-divider>
							<v-card-actions>
								<v-btn color="primary darken-1" text @click="showCustomResults = false">
									<translate>Close</translate>
								</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
				</v-tooltip>
				<v-spacer></v-spacer>
				<v-tooltip top>
					<template v-slot:activator="{ on }">
						<v-btn icon @click="showEdit = true" v-on="on">
							<v-icon color="primary">edit</v-icon>
						</v-btn>
					</template>
					<span v-translate>Edit</span>
				</v-tooltip>
				<template v-if="type == 'tcm'">
					<template v-if="showEdit">
						<TCMTongueForm
							key="edit-tongue"
							v-if="test.kind == 'tongue'"
							@edit-test="editTest"
							@hide="showEdit = false"
							:test="test"
							:edit="true"
						/>
						<TCMPulseForm
							v-else-if="test.kind == 'pulse'"
							@edit-test="editTest"
							@hide="showEdit = false"
							:test="test"
							:edit="true"
						/>
						<TCMAbdominalPalpationForm
							v-else-if="test.kind == 'abdominal-palpation'"
							@edit-test="editTest"
							@hide="showEdit = false"
							:test="test"
							:edit="true"
						/>
						<TCMBodyStructureForm
							v-else-if="test.kind == 'body-structure'"
							@edit-test="editTest"
							@hide="showEdit = false"
							:test="test"
							:edit="true"
						/>
					</template>
				</template>
				<v-dialog v-else v-model="showEdit" :max-width="type == 'osteo' ? '95%' : '80%'" scrollable>
					<CustomTestForm
						v-if="type === 'custom'"
						@edit-test="editTest"
						@hide="showEdit = false"
						:test="test"
						:edit="true"
					/>
					<VitalSignsForm
						v-if="type === 'general'"
						@edit-test="editTest"
						@hide="showEdit = false"
						:test="test.data"
						:edit="true"
					/>
					<RyodorakuForm
						v-if="type === 'ryodoraku'"
						@edit-test="editTest"
						@hide="showEdit = false"
						:test="test.data"
						:edit="true"
					/>
					<OsteopathyForm
						v-if="type === 'osteo'"
						@edit-test="editTest"
						@hide="showEdit = false"
						:test="test.data"
						:edit="true"
						:gender="gender"
						ref="osteoFormEdit"
					/>
				</v-dialog>
				<v-tooltip top>
					<template v-slot:activator="{ on }">
						<v-btn icon @click="deleteTest" v-on="on">
							<v-icon color="primary">delete</v-icon>
						</v-btn>
					</template>
					<span v-translate>Remove</span>
				</v-tooltip>
			</v-card-actions>
		</v-card>
	</div>
</template>

<script>
import RyodorakuForm from "@/components/appointment/tests/ryodoraku/RyodorakuForm.vue";
import RyodorakuResults from "@/components/appointment/tests/ryodoraku/RyodorakuResults.vue";
import VitalSignsForm from "@/components/appointment/tests/general/VitalSignsForm.vue";
import TCMTongueForm from "@/components/appointment/tests/tcm/TCMTongueForm.vue";
import TCMPulseForm from "@/components/appointment/tests/tcm/TCMPulseForm.vue";
import TCMPulseDetails from '@/components/appointment/tests/tcm/TCMPulseDetails.vue';
import TCMAbdominalPalpationForm from "@/components/appointment/tests/tcm/TCMAbdominalPalpationForm.vue";
import TCMBodyStructureForm from "@/components/appointment/tests/tcm/TCMBodyStructureForm.vue";
import OsteopathyForm from "@/components/appointment/tests/osteo/OsteopathyForm.vue";
import TongueTemplate from "@/components/drawing/TongueTemplate.vue";
import TongueDetails from "@/components/drawing/TongueDetails.vue";
import CustomTestForm from "@/components/appointment/tests/custom/CustomTestForm.vue";
import ICDList from "@/components/icd/ICDList.vue";
import axios from "axios";
import Vue from 'vue';
import OsteopathyDetails from './osteo/OsteopathyDetails.vue';

export default {
	name: "TestItem",
	props: ["test", "type", "gender"],
	components: {
		RyodorakuForm,
		RyodorakuResults,
		VitalSignsForm,
		TCMTongueForm,
		TCMPulseForm,
		TCMPulseDetails,
		TCMAbdominalPalpationForm,
		TCMBodyStructureForm,
		OsteopathyForm,
		OsteopathyDetails,
		TongueTemplate,
		TongueDetails,
		CustomTestForm,
		ICDList
	},
	data: () => ({
		showResults: false,
		showCustomResults: false,
		loadingCustomResults: false,
		loadingResults: false,
		showEdit: false,
		render: 1,
		icdList: []
	}),
	methods: {
		editTest(form) {
			this.$emit("edit-test", this.test, form);
		},
		deleteTest() {
			this.$emit("delete-test", this.test);
		},
		getCustomResults() {
			const appointmentId = this.$route.params.aid;
			const patientId = this.$route.params.pid;
			this.loadingCustomResults = true;
			axios
				.get(
					"/patient/" +
						patientId +
						"/appointment/" +
						appointmentId +
						"/custom-tests/" +
						this.test.id +
						"/results"
				)
				.then(response => {
					console.log(response.data);
					this.icdList = response.data;
					this.showCustomResults = true;
					this.loadingCustomResults = false;
				})
				.catch(error => {
					console.log(error);
					this.loadingCustomResults = false;
				});
		},
		getResults() {
			if(this.test.module == 'ryodoraku' || this.test.kind == 'pulse') {
				const appointmentId = this.$route.params.aid;
				const patientId = this.$route.params.pid;
				this.loadingResults = true;
				axios
					.get(
						"/patient/" +
						patientId +
						"/appointment/" +
						appointmentId +
						"/exam/" +
						this.test.id +
						"/results"
					)
					.then(response => {
						console.log(response.data);
						Vue.set(this.test.result, 'results', response.data.result);
						this.showResults = true;
						this.loadingResults = false;
					})
					.catch(error => {
						console.log(error);
						this.loadingResults = false;
					});
			} else this.showResults = true;
		}
	},
	watch: {
		showResults(value) {
			if(value && this.type == 'osteo' && this.$refs.osteoFormResults) this.$refs.osteoFormResults.refresh();
		},
		showEdit(value) {
			if(value && this.type == 'osteo' && this.$refs.osteoFormEdit) this.$refs.osteoFormEdit.refresh();
		},
		test() {
			this.render += 1;
		}
	}
};
</script>